import { forwardRef, useState, useMemo } from "react";
import { PasswordInput, Progress, Popover, Box, Text } from "@mantine/core";
import useIsDesktop from "../../../hooks/useIsDesktop";
import { HiCheck, HiXMark } from "react-icons/hi2";

// Default password requirements
const defaultRequirements = [
  { re: /[0-9]/, label: "Includes number" },
  { re: /[a-z]/, label: "Includes lowercase letter" },
  { re: /[A-Z]/, label: "Includes uppercase letter" },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: "Includes special symbol" },
];

function PasswordRequirement({ meets, label, isDesktop }) {
  const iconSize = isDesktop ? 14 : 12;
  return (
    <Text
      c={meets ? "teal" : "red"}
      style={{ display: "flex", alignItems: "center" }}
      mt={isDesktop ? 7 : 4}
      size={isDesktop ? "sm" : "xs"}
    >
      {meets ? <HiCheck size={iconSize} /> : <HiXMark size={iconSize} />}
      <Box component="span" ml={isDesktop ? 10 : 7}>
        {label}
      </Box>
    </Text>
  );
}

function getStrength(password, requirements, minLength) {
  let multiplier = password.length >= minLength ? 0 : 1;

  requirements.forEach((req) => {
    if (!req.re.test(password)) multiplier += 1;
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

const CustomPasswordInput = forwardRef(
  (
    {
      descriptionProps = {},
      labelProps = {
        style: { fontWeight: "700" },
        className: "text-neutral-500",
      },
      errorProps = {},
      requirements = defaultRequirements,
      minLength = 6,
      showStrengthMeter = true,
      popoverPosition = "bottom",
      size,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const isDesktop = useIsDesktop();
    const responsiveSize = size ?? (isDesktop ? "md" : "sm");
    const [popoverOpened, setPopoverOpened] = useState(false);

    const checks = useMemo(
      () =>
        requirements.map((req, index) => (
          <PasswordRequirement
            key={index}
            label={req.label}
            meets={req.re.test(value || "")}
            isDesktop={isDesktop}
          />
        )),
      [requirements, value, isDesktop]
    );

    const strength = useMemo(
      () => getStrength(value || "", requirements, minLength),
      [value, requirements, minLength]
    );

    const color = strength === 100 ? "teal" : strength > 50 ? "yellow" : "red";

    if (showStrengthMeter) {
      return (
        <Popover
          opened={popoverOpened}
          position={popoverPosition}
          width="target"
          transitionProps={{ transition: "pop" }}
        >
          <Popover.Target>
            <span
              onFocusCapture={() => setPopoverOpened(true)}
              onBlurCapture={() => setPopoverOpened(false)}
            >
              <PasswordInput
                labelProps={labelProps}
                descriptionProps={descriptionProps}
                errorProps={errorProps}
                ref={ref}
                size={responsiveSize}
                value={value}
                onChange={onChange}
                {...props}
              />
            </span>
          </Popover.Target>
          <Popover.Dropdown>
            <Progress color={color} value={strength} size={isDesktop ? 5 : 4} mb="xs" />
            <PasswordRequirement
              label={`Includes at least ${minLength} characters`}
              meets={(value || "").length >= minLength}
              isDesktop={isDesktop}
            />
            {checks}
          </Popover.Dropdown>
        </Popover>
      );
    }

    return (
      <PasswordInput
        labelProps={labelProps}
        descriptionProps={descriptionProps}
        errorProps={errorProps}
        ref={ref}
        size={responsiveSize}
        value={value}
        onChange={onChange}
        {...props}
      />
    );
  }
);

export default CustomPasswordInput;
